import { AlertTriangle, Loader2, Plus, Video } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui";
import { ROUTES } from "@/constants/routes";
import { useGetRunsQuery } from "@/services/api-hooks/content";
import { gtmService } from "@/services/gtm-service";
import { RunStatus, type UseCaseRun } from "@/types/content";
import { getRunImageUrls, parseRunOutputs } from "@/utils/run";

import { CarouselLightbox } from "./components/CarouselLightbox";
import { InAppDownloadDialog } from "./components/InAppDownloadDialog";
import { Pagination } from "./components/Pagination";
import { RunDetailsModal } from "./components/RunDetailsModal";
import { VideoCard } from "./components/VideoCard";
import { VideoModal } from "./components/VideoModal";
import { downloadFile, isInAppBrowser } from "./utils";

const PAGE_SIZE = 20;

export function Videos() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useGetRunsQuery(page, PAGE_SIZE);
  const runs = data?.items ?? [];
  const total = data?.total ?? 0;

  const navigate = useNavigate();
  const [viewerRun, setViewerRun] = useState<UseCaseRun | null>(null);
  const [detailRun, setDetailRun] = useState<UseCaseRun | null>(null);
  const [inAppDownloadOpen, setInAppDownloadOpen] = useState(false);

  // Fire generation outcome analytics when a run transitions out of an active state.
  const prevStatusRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    for (const run of runs) {
      const prev = prevStatusRef.current.get(run.run_id);
      const wasActive = prev === RunStatus.ACCEPTED || prev === RunStatus.RUNNING;
      if (!wasActive) continue;

      if (run.status === RunStatus.COMPLETED) {
        const outputs = parseRunOutputs(run.outputs_json);
        gtmService.trackGenContentScs({
          content_id: run.run_id,
          length: outputs.duration_seconds ?? getRunImageUrls(outputs).length,
          gen_time: run.duration_seconds ?? 0,
          gen_price: run.cost_usd ?? 0,
        });
      } else if (run.status === RunStatus.FAILED) {
        gtmService.trackGenContentFail({
          content_id: run.run_id,
          stage: "generation",
          error: run.error ?? "generation_failed",
        });
      }
    }
    prevStatusRef.current = new Map(runs.map((r) => [r.run_id, r.status]));
  }, [runs]);

  const handleSelect = (run: UseCaseRun) => {
    if (run.status !== RunStatus.COMPLETED) return;
    const outputs = parseRunOutputs(run.outputs_json);
    if (outputs.video_url || getRunImageUrls(outputs).length > 0) setViewerRun(run);
  };

  const handleDownloadImage = async (runId: string, url: string) => {
    gtmService.trackContentDownload({ content_id: runId });
    await downloadFile(url);
  };

  const handleDownloadVideo = async (runId: string, url: string) => {
    gtmService.trackContentDownload({ content_id: runId });
    if (isInAppBrowser()) {
      setInAppDownloadOpen(true);
      return;
    }
    await downloadFile(url);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-50">
          <AlertTriangle className="h-8 w-8 text-red-500" />
        </div>
        <div>
          <h3 className="font-semibold text-lg text-zinc-700">
            Couldn't load your content
          </h3>
          <p className="mt-1 text-sm text-zinc-400">
            Something went wrong while fetching your content. Please try again.
          </p>
        </div>
        <Button variant="default" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const viewerOutputs = viewerRun ? parseRunOutputs(viewerRun.outputs_json) : null;
  const viewerImages = viewerOutputs ? getRunImageUrls(viewerOutputs) : [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-10">
      <div className="mb-6 flex items-center justify-between gap-4 sm:mb-8">
        <div className="min-w-0">
          <h1 className="font-bold text-2xl text-zinc-900 sm:text-3xl">Content</h1>
          <p className="mt-1 text-sm text-zinc-500">
            {total} item{total !== 1 ? "s" : ""} generated
          </p>
        </div>
        <Button asChild variant="default">
          <Link to={ROUTES.CREATE}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New Content</span>
            <span className="sm:hidden">New</span>
          </Link>
        </Button>
      </div>

      {runs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-zinc-300 border-dashed py-20">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-100">
            <Video className="h-8 w-8 text-zinc-400" />
          </div>
          <h3 className="font-semibold text-lg text-zinc-700">No content yet</h3>
          <p className="mt-1 mb-6 text-sm text-zinc-400">
            Create your first AI twin content
          </p>
          <Link
            to={ROUTES.CREATE}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 font-semibold text-sm text-white no-underline shadow-md shadow-primary/25 hover:bg-primary-hover"
          >
            <Plus className="h-4 w-4" /> Create
          </Link>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {runs.map((run) => (
              <VideoCard
                key={run.run_id}
                run={run}
                outputs={parseRunOutputs(run.outputs_json)}
                onSelect={() => handleSelect(run)}
                onRegenerate={() => navigate(`${ROUTES.CREATE}?from=${run.run_id}`)}
                onDownload={(url) => handleDownloadVideo(run.run_id, url)}
                onDetails={() => setDetailRun(run)}
              />
            ))}
          </div>
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
        </>
      )}

      {detailRun && (
        <RunDetailsModal run={detailRun} onClose={() => setDetailRun(null)} />
      )}

      <InAppDownloadDialog open={inAppDownloadOpen} onOpenChange={setInAppDownloadOpen} />

      {viewerRun && !viewerOutputs?.video_url && viewerImages.length > 0 && (
        <CarouselLightbox
          urls={viewerImages}
          onClose={() => setViewerRun(null)}
          onDownload={(url) => handleDownloadImage(viewerRun.run_id, url)}
        />
      )}

      {viewerRun && viewerOutputs?.video_url && (
        <VideoModal
          url={viewerOutputs.video_url}
          durationSeconds={viewerOutputs.duration_seconds}
          onClose={() => setViewerRun(null)}
          onDownload={(url) => handleDownloadVideo(viewerRun.run_id, url)}
        />
      )}
    </div>
  );
}
